import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Lock, ArrowRight, MousePointer2, Sparkles } from 'lucide-react';

const A = '/path-world';

// Adventure stations placed along the path (frac = position along the scroll journey)
const STATIONS = [
  { key: 'vent', frac: 0.16, live: true, title: 'Vent to a Dog', tag: 'LIVE', blurb: 'No judgement. No advice. Just a dog who listens.', to: '/vent-to-dog' },
  { key: 'dare', frac: 0.42, live: false, title: 'Darecations', tag: 'SOON', blurb: 'Tiny dares that pull you off the feed and into the world.' },
  { key: 'wonder', frac: 0.66, live: false, title: 'Wondercations', tag: 'SOON', blurb: 'Chase awe — hidden trails, night skies, small wonders.' },
  { key: 'surprise', frac: 0.90, live: false, title: 'Surprisecations', tag: 'SOON', blurb: 'Let the wanderlust agent surprise you. Trust the path.' },
];

const clamp01 = (n) => Math.max(0, Math.min(1, n));

export default function PathWorld() {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(0);
  const [vw, setVw] = useState(typeof window !== 'undefined' ? window.innerWidth : 1280);
  const rafRef = useRef(0);

  const onScroll = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(clamp01(max > 0 ? window.scrollY / max : 0));
    });
  }, []);

  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      cancelAnimationFrame(rafRef.current);
    };
  }, [onScroll]);

  const GROUND_TRAVEL = vw * 3.4;       // total world travel in px
  const BX = vw * 0.34;                 // beetle screen x (where stations "arrive")

  // layer parallax shift helper
  const shift = (speed) => -progress * GROUND_TRAVEL * speed;

  // which station is at the beetle right now
  let active = -1;
  STATIONS.forEach((s, i) => {
    if (Math.abs(s.frac - progress) < 0.07) active = i;
  });

  const layer = (img, speed, repeat = true) => ({
    backgroundImage: `url(${A}/${img})`,
    backgroundRepeat: repeat ? 'repeat-x' : 'no-repeat',
    backgroundSize: repeat ? 'auto 100%' : 'cover',
    backgroundPositionX: `${shift(speed)}px`,
    backgroundPositionY: 'bottom',
  });

  return (
    <div style={{ height: '480vh' }} className="relative bg-[#0c1410]" data-testid="path-world">
      {/* Sticky stage */}
      <div className="sticky top-0 h-screen w-full overflow-hidden">
        {/* Sky (slowest, non-repeating cover, no shift to avoid edge gaps) */}
        <div className="absolute inset-0" style={{ backgroundImage: `url(${A}/sky.png)`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }} />
        <div className="absolute inset-0 bg-gradient-to-b from-amber-50/10 via-transparent to-[#0c1410]/70 pointer-events-none" />

        {/* Far hills */}
        <div className="absolute left-0 right-0 bottom-[26%] h-[48%]" style={layer('far_hills.png', 0.22)} />
        {/* Mid trees */}
        <div className="absolute left-0 right-0 bottom-[16%] h-[46%]" style={layer('mid_trees.png', 0.5)} />
        {/* Ground / path */}
        <div className="absolute left-0 right-0 bottom-0 h-[30%]" style={layer('ground.png', 1)} />

        {/* Stations on the path */}
        {STATIONS.map((s, i) => {
          const left = BX + (s.frac - progress) * GROUND_TRAVEL;
          if (left < -360 || left > vw + 360) return null;
          const isActive = active === i;
          return (
            <StationNode
              key={s.key}
              station={s}
              left={left}
              isActive={isActive}
              onEnter={() => s.live && s.to && navigate(s.to)}
            />
          );
        })}

        {/* Beetle actively rolling the green world-ball — spinning ball + 2-frame leg cycle */}
        <div className="absolute z-30 select-none" style={{ left: BX, bottom: '18%', transform: 'translateX(-52%)' }} data-testid="path-beetle">
          <div className="beetle-push relative" style={{ width: 'clamp(170px, 20vw, 290px)' }}>
            {/* the rolling ball (behind the beetle's legs) */}
            <img
              src={`${A}/ball.png`}
              alt=""
              className="ball-roll absolute z-10"
              style={{ width: '70%', right: '-26%', bottom: '13%', filter: 'drop-shadow(0 7px 11px rgba(0,0,0,0.4))' }}
            />
            {/* leg-cycle frames stacked; frame B toggles over frame A */}
            <img src={`${A}/beetle_a.png`} alt="The dung beetle" className="relative z-20 w-full" style={{ filter: 'drop-shadow(0 8px 12px rgba(0,0,0,0.42))' }} />
            <img src={`${A}/beetle_b.png`} alt="" className="leg-cycle absolute inset-0 z-20 w-full" style={{ filter: 'drop-shadow(0 8px 12px rgba(0,0,0,0.42))' }} />
            {/* soft ground shadow */}
            <div className="absolute -bottom-1 left-1/2 w-[120%] h-3 rounded-full bg-black/30 blur-md z-0" style={{ transform: 'translateX(-44%)' }} />
          </div>
        </div>

        {/* Foreground grass (fastest) */}
        <div className="absolute left-0 right-0 bottom-0 h-[15%] z-40 pointer-events-none" style={layer('fg_grass.png', 1.35)} />

        {/* ===== Y2K HUD ===== */}
        <TopBar onBack={() => navigate('/')} />
        <Trail progress={progress} active={active} />
        <ScrollHint progress={progress} />
      </div>
    </div>
  );
}

/* ---------------- Station node ---------------- */
const StationNode = ({ station, left, isActive, onEnter }) => {
  return (
    <div className="absolute z-20" style={{ left, bottom: '24%', transform: 'translateX(-50%)' }}>
      {/* signpost glow on the path */}
      <div className="relative flex flex-col items-center">
        <AnimatePresence>
          {isActive && (
            <motion.div
              initial={{ opacity: 0, y: 16, scale: 0.92 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.92 }}
              transition={{ type: 'spring', stiffness: 260, damping: 22 }}
              className="absolute bottom-[140px] w-[min(82vw,320px)]"
              data-testid={`station-card-${station.key}`}
            >
              <div className="chrome-card rounded-2xl p-[2px]">
                <div className="rounded-2xl bg-[#0c1712]/85 backdrop-blur-md px-5 py-4 text-center">
                  <span className={`inline-block text-[10px] font-mono tracking-[0.3em] px-2 py-0.5 rounded-full mb-2 ${station.live ? 'bg-emerald-400/20 text-emerald-300' : 'bg-white/10 text-white/50'}`}>
                    {station.tag}
                  </span>
                  <h3 className="font-heading font-bold text-2xl text-white leading-tight">{station.title}</h3>
                  <p className="text-sm text-white/65 mt-1.5 mb-3">{station.blurb}</p>
                  {station.live ? (
                    <button
                      onClick={onEnter}
                      data-testid={`station-enter-${station.key}`}
                      className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-400 text-[#08130d] font-heading font-bold text-sm hover:scale-[1.04] transition-transform"
                    >
                      Step in <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/8 text-white/45 font-mono text-xs tracking-widest">
                      <Lock className="w-3.5 h-3.5" /> ROLLING IN SOON
                    </span>
                  )}
                </div>
              </div>
              {/* little tail */}
              <div className="mx-auto w-3 h-3 rotate-45 -mt-1.5 bg-[#0c1712]/85 border-r border-b border-white/10" />
            </motion.div>
          )}
        </AnimatePresence>

        {/* portal / marker on the ground */}
        <motion.div
          animate={{ scale: isActive ? 1.12 : 1, opacity: isActive ? 1 : 0.7 }}
          transition={{ type: 'spring', stiffness: 200, damping: 18 }}
          className="relative"
        >
          <div className={`w-[64px] h-[64px] rounded-full flex items-center justify-center ${station.live ? 'bg-emerald-400/15 border border-emerald-300/40' : 'bg-white/5 border border-white/15'}`}>
            {station.live ? <Sparkles className="w-6 h-6 text-emerald-300" /> : <Lock className="w-5 h-5 text-white/40" />}
          </div>
          {station.live && (
            <span className="absolute inset-0 rounded-full border border-emerald-300/40 animate-ping-slow" />
          )}
          {/* label under marker when not active */}
          {!isActive && (
            <p className="absolute -bottom-7 left-1/2 -translate-x-1/2 whitespace-nowrap text-[11px] font-mono tracking-widest text-white/55">
              {station.title}
            </p>
          )}
        </motion.div>
      </div>
    </div>
  );
};

/* ---------------- HUD pieces ---------------- */
const TopBar = ({ onBack }) => (
  <div className="absolute top-0 left-0 right-0 z-50 flex items-center justify-between px-5 py-4">
    <button onClick={onBack} data-testid="path-back-btn" className="chrome-card rounded-full p-[1.5px] hover:scale-105 transition-transform">
      <span className="flex items-center gap-2 rounded-full bg-[#0c1712]/80 backdrop-blur px-3.5 py-2 text-white/80 text-xs font-mono tracking-widest">
        <ArrowLeft className="w-3.5 h-3.5" /> EXIT
      </span>
    </button>
    <div className="chrome-card rounded-full p-[1.5px]">
      <span className="block rounded-full bg-[#0c1712]/80 backdrop-blur px-4 py-2 text-white/85 text-xs font-mono tracking-[0.35em]">
        THE&nbsp;PATH
      </span>
    </div>
    <div className="w-[68px]" />
  </div>
);

const Trail = ({ progress, active }) => (
  <div className="absolute top-16 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2.5" data-testid="path-trail">
    {STATIONS.map((s, i) => (
      <div key={s.key} className="flex items-center gap-2.5">
        <span className={`w-2 h-2 rounded-full transition-all duration-300 ${active === i ? 'scale-150 bg-emerald-300' : progress > s.frac ? 'bg-emerald-300/60' : 'bg-white/25'}`} />
        {i < STATIONS.length - 1 && <span className="w-6 h-px bg-white/15" />}
      </div>
    ))}
  </div>
);

const ScrollHint = ({ progress }) => (
  <AnimatePresence>
    {progress < 0.04 && (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 10 }}
        className="absolute bottom-7 left-1/2 -translate-x-1/2 z-50 flex flex-col items-center gap-2 text-white/70"
        data-testid="path-scroll-hint"
      >
        <MousePointer2 className="w-4 h-4 animate-bounce" />
        <span className="text-[11px] font-mono tracking-[0.3em] uppercase">Scroll to walk the path</span>
      </motion.div>
    )}
  </AnimatePresence>
);
